import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { DealCard } from '@/components/DealCard';
import { Skeleton } from '@/components/Skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { SAVED_DEALS_CAP, SavedDealCapError } from '@/lib/db/buyer';

import {
  SAVED_DEALS_WARNING_THRESHOLD,
  useRemoveSavedDeal,
  useSavedCount,
  useSavedDeals,
} from './DashboardSavedPage.data';

type SortKey = 'date' | 'discount' | 'price';

export function DashboardSavedPage() {
  const { t } = useTranslation();
  const auth = useAuth();
  const [sort, setSort] = useState<SortKey>('date');

  const userId = auth.status === 'authenticated' ? auth.user.id : null;
  const { state, refresh } = useSavedDeals(sort);
  const { state: countState, refresh: refreshCount } = useSavedCount(userId);
  const { remove } = useRemoveSavedDeal(userId);

  const handleRemove = async (dealId: string) => {
    try {
      const ok = await remove(dealId);
      if (!ok) return;
      toast.success(t('buyer.saved.removedToast'));
      refresh();
      refreshCount();
    } catch (err) {
      if (err instanceof SavedDealCapError) {
        toast.error(t('buyer.saved.errors.capReached'));
        return;
      }
      toast.error(t('buyer.errors.generic'));
    }
  };

  return (
    <>
      <header className="buyer-page-head">
        <div>
          <h1 className="buyer-page-head__title">{t('buyer.saved.title')}</h1>
          <p className="buyer-page-head__sub">
            {state.status === 'success'
              ? t('buyer.saved.count', { count: state.deals.length })
              : t('buyer.saved.countLoading')}
          </p>
        </div>
        {state.status === 'success' && state.deals.length > 0 ? (
          <div className="buyer-saved-toolbar">
            <span className="buyer-saved-toolbar__label">{t('buyer.saved.sortLabel')}</span>
            <select
              value={sort}
              onChange={(event) => setSort(event.target.value as SortKey)}
              className="buyer-saved-toolbar__select"
              aria-label={t('buyer.saved.sortLabel')}
            >
              <option value="date">{t('buyer.saved.sortDate')}</option>
              <option value="discount">{t('buyer.saved.sortDiscount')}</option>
              <option value="price">{t('buyer.saved.sortPrice')}</option>
            </select>
          </div>
        ) : null}
      </header>

      {countState.status === 'success' && countState.count >= SAVED_DEALS_WARNING_THRESHOLD ? (
        <div className="buyer-saved-warning" role="status">
          {t('buyer.saved.warnings.approachingCap', {
            count: countState.count,
            cap: SAVED_DEALS_CAP,
          })}
        </div>
      ) : null}

      {state.status === 'loading' ? (
        <div className="buyer-saved-grid">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} height={320} />
          ))}
        </div>
      ) : null}

      {state.status === 'error' ? (
        <div className="buyer-empty" role="alert">
          <h2 className="buyer-empty__title">{t('buyer.saved.errorTitle')}</h2>
          <p className="buyer-empty__lead">{t('buyer.saved.errorLead')}</p>
          <button type="button" className="m5-btn m5-btn--primary" onClick={refresh}>
            {t('buyer.saved.retry')}
          </button>
        </div>
      ) : null}

      {state.status === 'success' && state.deals.length === 0 ? (
        <div className="buyer-empty">
          <h2 className="buyer-empty__title">{t('buyer.saved.emptyTitle')}</h2>
          <p className="buyer-empty__lead">{t('buyer.saved.emptyLead')}</p>
          <Link to="/" className="m5-btn m5-btn--primary">
            {t('buyer.saved.browse')}
          </Link>
        </div>
      ) : null}

      {state.status === 'success' && state.deals.length > 0 ? (
        <div className="buyer-saved-grid">
          {state.deals.map((deal) => (
            <DealCard
              key={deal.id}
              deal={deal}
              showRemoveButton
              onRemove={() => {
                void handleRemove(deal.id);
              }}
            />
          ))}
        </div>
      ) : null}
    </>
  );
}
